














































import Vue from 'vue'
import { debounce } from 'debounce'
import { mapGetters } from 'vuex'
import IListActionMenu from '@/components/IListActionMenu.vue'
import groupMixin from '@/mixins/group-mixin'
import blockedMixin from '@/mixins/blocked-contacts-mixin'
import Group from '@/types/Group.interface'
import Person from '@/types/Person.interface'
import PersonProfile from '@/components/PersonProfile.vue'
import Button from '@/types/components/Button.interface'
import IListAction, { IListActionType } from '@/types/components/IListAction.interface'
import IListTile from '@/types/components/IListTile.interface'
import IListIcon, { IListIconType } from '@/types/components/IListIcon.interface'
import IList from '@/types/components/IList.interface'
import ScrollPosition from '@/types/components/ScrollPosition.interface'
export default Vue.extend({
    components: { IListActionMenu, PersonProfile },
    mixins: [groupMixin, blockedMixin],
    props: ['id'],
    data() {
        return {
            //  The select filter shown in the left-top slot
            filter: {
                selected: null,
                options: []
            },
            //  Tracks our loading state
            loading: false,
            //  Our placeholder for when we don't have any persons
            personsListPlaceholder: {
                img: 'noCustomers',
                content: 'This group has no members'
            },
            //  Determines if we were searching for a contact
            searched: false,
            //  Contains our temporary list of contacts
            searchResults: null,
            //  Holds the text in the searchbox
            searchtext: null,
            //  Determines how many static contacts we display
            staticPageNumber: 1,
            //  Tracks if the component has been fully initialized
            mounted: false
        }
    },
    beforeDestroy() {
        //  When leaving, we want to unset our selected group and selected persons (the watcher takes care of the rest)
        this.unsetGroup()
    },
    beforeRouteLeave(to, from, next) {
        //  Check if changes have been made to the Person
        this.interuptNavigation(next)
    },
    async mounted() {
        //  Check if a group is set in the url params
        const id = this.id

        //  If a group id is set, load it
        if (id) await this.$store.dispatch('listsSelectedGroupGet', id)

        //  We use this mounted variable to track when dom elements have been rendered
        this.mounted = true
    },
    computed: {
        ...mapGetters({
            isApp: 'isApp',
            params: 'listsSelectedGroupPersonsParams',
            lastPage: 'listsSelectedGroupPersonsLastPage',
            scrollPositionLeft: 'scrollPositionLeft',
            paginating: 'listsPaginating'
        }),
        //  Actions available to modify the group
        groupActions(): Button[] {
            //  Create our array of buttons
            const menu: Button[] = [
                {
                    text: 'Edit group',
                    color: 'primary',
                    icon: 'mdi-square-edit-outline',
                    callback: () => this.goToGroupEdit(this.selectedGroup),
                    hidden: this.selectedGroup.required
                },
                {
                    text: 'Copy group',
                    color: 'primary',
                    icon: 'mdi-content-copy',
                    callback: () => this.groupCopy(this.selectedGroup),
                    hidden: !this.selectedGroup.persons_count
                },
                {
                    text: 'Export group',
                    color: 'primary',
                    icon: 'mdi-arrow-collapse-down',
                    callback: () => this.groupExport(this.selectedGroup),
                    hidden: this.isApp || this.$store.getters.isMobile()
                },
                {
                    text: 'Delete group',
                    color: 'primary',
                    icon: 'bi-trash-fill',
                    callback: () =>
                        this.groupDelete(
                            this.selectedGroup,
                            this.$router.push('/groups')
                        ),
                    hidden: this.selectedGroup.required
                }
            ]
            //  Return our IListAction menu
            return menu
        },
        maxStaticPageNumber(): number {
            //  Grab our current group
            const group = this.selectedGroup

            //  If the group is null OR has a query, return 0
            if (!group || group.query !== null) return 0

            //  Else, grab our total number of persons
            const persons = this.$store.getters.listsSelectedGroupPersons

            //  Get the total from this
            const total = persons.length

            //  Divide this number by 25 and round up
            const maxNum = Math.ceil(total / 25)

            //  Return this maximum
            return maxNum
        },
        multiSelectedPersonIds(): number[] {
            //  If the personsIList does NOT exist, return empty array
            if (!this.personsIList) return []

            //  Assemble an array of multi-selected person ids
            const ids = this.personsIList.multiSelected.map((person: Person) => person.id)

            //  Return the person ids
            return ids
        },
        operator(): boolean {
            //  Grab our current role
            const role = this.$store.getters.userRole(this.$store.getters.user)

            //  Check if this role is an operator
            return window.getProps(role, ['name']) === 'Operator'
        },
        optOutGroup(): Group {
            return this.$store.getters.optOutGroup
        },
        personsIList(): IList {
            return this.$store.getters.iList('listsPersonsList')
        },
        //  The v-model for group member search text
        search: {
            get(): string {
                return this.searchtext
            },
            set: debounce(function (val: string): void {
                //  Set the searchtext state
                this.searchtext = val
                this.personsSearch()
            }, 750)
        },
        //  The currently selected group
        selectedGroup: {
            get(): Group {
                return this.$store.getters.listsSelectedGroup
            },
            set(group: Group): void {
                this.$store.commit('listsSelectedGroupSet', group)
            }
        },
        queryGroupPersons(): Person[] {
            //  Grab our selected group
            const group = this.selectedGroup

            //  If the group doesn't exist OR is static, return empty array
            if (!group || !group.query) return []

            //  Our list is our selected persons for this group
            const list = this.$store.getters.listsSelectedGroupPersons

            //  Just return our list of persons
            return list
        },
        staticGroupPersons(): Person[] {
            //  Grab our selected group
            const group = this.selectedGroup

            //  If the group doesn't exist OR is a query group, return empty array
            if (!group || group.query !== null) return []

            //  Our list is our selected persons for this group
            const list = this.$store.getters.listsSelectedGroupPersons || []

            //  Grab our current page number
            const page = this.staticPageNumber

            //  Based on this page number, we want n * 25 of these contacts to appear
            const total = page * 25

            //  GRab this subset of contacts
            return list.slice(0, total)
        },
        selectedGroupPersons: {
            get(): Person[] {
                //  Grab our group
                const group = this.selectedGroup

                //  If no group then return empty array
                if (!group) return []

                //  Based on our group type, grab our persons
                const persons = group.query
                    ? this.queryGroupPersons
                    : this.staticGroupPersons

                //  Our list depends on if we are searching or not
                const list = this.searched ? this.searchResults : persons

                //  If this is NOT the All MessageDesk Users group then just return our list
                if (group.name !== 'All MessageDesk Users') return list

                //  Else, we need to filter
                return this.$store.getters.withoutSupport(list)
            },
            set(val: Person[]): void {
                this.$store.commit('listsSelectedGroupPersonsSet', val)
            }
        },
        //  The currently selected group persons selected person
        selectedPerson: {
            get(): Person {
                return this.$store.getters.listsSelectedPerson
            },
            set(person: Person): void {
                this.$store.commit('listsSelectedPersonSet', person)
            }
        },
        showSendBroadcast(): boolean {
            if (this.optOutGroup && this.selectedGroup)
                return this.optOutGroup.id !== this.selectedGroup.id
        },
        //  The toolbar title
        toolbar(): object {
            //  If a group does NOT exist, return empty object (so toolbar fills its space)
            if (!this.selectedGroup) return {}

            //  Else, get group attributes
            const type = this.$options.filters.groupType(this.selectedGroup)
            const icon = this.$options.filters.groupIcon(this.selectedGroup)

            //  Return parse toolbar bar title
            return {
                title: this.selectedGroup.name,
                subtitle: `<span class="mdi ${icon}"/><span class="ml-1">${type}</span>`
            }
        }
    },
    methods: {
        //  Gets the unsubscribe message from this group IF it is an autoresponder group
        async getUnsubMsg(): Promise<string> {
            //  Grab our campaign id from our group
            const campaign_id = this.selectedGroup.campaign_id

            //  If the group does NOT have a campaign id, return empty string
            if (!campaign_id) return ''

            //  Get the campaign for this autoresponder IF it is an autoresponder
            const campaign = await this.$store.dispatch('campaignsGet', campaign_id)

            //  If no campaign got returned then we just return an empty string
            if (!campaign) return ''

            //  Return our campaign's unsubscribe messages
            return campaign.unsubscribe_message
        },
        //  Actions displayed on the person tile
        groupPersonActions(person: Person): IListAction {
            //  Get our selected group
            const group = this.selectedGroup

            //  If no group, return null
            if (!group) return null

            //  Else, grab the campaign id
            const campaign_id = group.campaign_id

            //  Get the group type
            const groupType = campaign_id ? 'Autoresponder' : 'Group'

            //  Get default actions
            const menu: Button[] = [
                {
                    icon: 'mdi-message-processing',
                    color: 'green',
                    text: 'Send Message',
                    callback: () => {
                        //  Grab the person's channel id
                        const channel_id = person.primary_channel_id
                        //  Navigate to this channel
                        this.$router.push(`/inbox?channel=${channel_id}`)
                    }
                },
                {
                    icon: 'bi-trash-fill',
                    color: 'red',
                    text: `Remove from ${groupType}`,
                    callback: () => this.removePersonsFromGroupConfirm([person.id]),
                    disabledPopup: this.operator,
                    hidden: this.selectedGroup.required
                },
                {
                    icon: 'mdi-account-cancel',
                    text: this.blockedContactTooltip(person),
                    callback: () => this.toggleBlock(person),
                    disabledPopup: this.operator,
                    //  If the person has a default phone, add the 'block' toggle action (we do this to handle the rare occasion when a 'phone_default' does NOT exist)
                    hidden:
                        !person.phone_default ||
                        !this.$options.filters.phone(person.phone_default.msisdn)
                }
            ]

            //  Create our IListAction
            const action: IListAction = {
                type: IListActionType.menu,
                menu: menu
            }

            //  Return our IListAction
            return action
        },
        //  Our IListIcon displayed to the left of the tile
        groupPersonIcon(person: Person): IListIcon {
            //  Grab the icon for this person
            const icon = this.$options.filters.personIcon(person)

            //  Create our IListIcon object
            const listIcon: IListIcon = {
                type: IListIconType.icon,
                content: icon,
                color: 'primary'
            }

            //  Return our IListIcon object
            return listIcon
        },
        //  Interupt naviagation to check if we need to save chanes to the selected Person
        interuptNavigation(callback: Function) {
            //  Check if the currently loaded person has been changed (ie needs to be saved)
            const save = this.$refs.personProfile && this.$refs.personProfile.edited

            //  If we do NOT need to save changes, continue
            if (!save) return callback()

            //  Else, pop the confirm changes dialog
            this.$refs.personProfile.confirmChanges(callback)
        },
        onPersonSelected(person: Person): void {
            //  Check if the person is different from the currently selected person
            const isNew = !this.selectedPerson || this.selectedPerson.id != person.id

            //  The 'next' function contains all the stuff we need to do a contact is selected (this allows us to pass the handler to other components as a callback)
            const next = () => {
                //  Set the step to 2 (for mobile layout only)
                this.$refs.listsPanel.step = 2

                //  If the person is NOT different, we do nothing
                if (!isNew) return

                //  Else, get our new person
                this.$store.dispatch('listsSelectedPersonGet', person.id)
            }

            //  If this is a new person, we need to check for changes before continuing
            if (isNew) return this.interuptNavigation(next)

            //  Else, just navigate to step 2 of the mobile interface
            this.$refs.listsPanel.step = 2
        },
        //  Paginate the next page of the current filtered list
        async paginateCurrentList(): Promise<void> {
            //  Get current pagination params
            const params = this.params

            //  Determine the next page
            const nextPage = params.page + 1

            //  Determine is the page exists, or we're already at the end of pagination OR if query does not exist on params
            const shouldPaginate = nextPage <= this.lastPage || !params.query

            //  If we should NOT paginate, do nothing
            if (!shouldPaginate) return

            //  Grab our selected group query
            const query = this.selectedGroup.query

            //  Else, update pagination params
            params.page = nextPage
            params.query = query
            this.$store.commit('listsSelectedGroupPersonsParamsSet', params)

            //  Paginate the next page
            await this.$store.dispatch('listsSelectedGroupPersonsGet')
        },
        personsSearch(): void {
            //  If search text is null then reset
            if (!this.searchtext) {
                //  Set to false so we display regular list
                this.searched = false
                //  Set to null to get rid of our previous list of persons
                return (this.searchResults = null)
            }

            //  Else If the group is a query group, we have to search using the API
            if (this.selectedGroup.query) return this.searchQueryGroup()

            //  Else, search the static group
            this.searchStaticGroup()
        },
        //  Our IListTile2 appearance
        personTile(): IListTile {
            return {
                actionContent: (person: Person) => this.groupPersonActions(person),
                color: (person: Person) => this.$options.filters.personColor(person),
                leftIcon: (person: Person) => this.groupPersonIcon(person),
                leftFooter: (person: Person) => this.$options.filters.personType(person),
                line1: (person: Person) =>
                    this.$options.filters.personFullNameOrEmail(person),
                line2: (person: Person) => person.company,
                line3: (person: Person) =>
                    this.$options.filters.personDefaultPhone(person),
                callback: (person: Person) => this.onPersonSelected(person)
            }
        },
        //  Route a removal request to the proper removal type
        removePersonsFromGroup(person_ids: number[], flag = null): void {
            //  If a group does NOT exist, throw error
            if (!this.selectedGroup) throw 'A group must be set on the component'

            //  Check if the group is a query group
            const isQuery = this.selectedGroup.query

            //  If the group is a query group, we use exceptions
            if (isQuery) return this.removePersonsFromQueryGroup(person_ids)

            //  Else, remove the person from a static group
            this.removePersonsFromStaticGroup(person_ids, flag)
        },
        async removePersonsFromGroupConfirm(person_ids: number[]): Promise<void> {
            //  Type of group we are removing
            const type = this.selectedGroup.campaign_id ? ' <br/> Autoresponder' : 'Group'
            const title = this.selectedGroup.campaign_id ? 'Unsubscribe' : 'Remove'

            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: `${title} Contact(s) from ${type}`,
                icon: 'mdi-account-remove',
                component: 'group-removal-form',
                props: {
                    group: this.selectedGroup,
                    unsubMsg: await this.getUnsubMsg()
                },
                callback: (result: boolean) =>
                    this.removePersonsFromGroup(person_ids, result)
            })
        },
        //   Add a person to the query groups exception list
        async removePersonsFromQueryGroup(person_ids: number[]): Promise<void> {
            // Fetch the current exceptions, or default to empty array
            const exceptions = this.selectedGroup.query.exceptions || []

            //  Add the person to the exceptions list
            person_ids.forEach((x) => exceptions.push(x))

            //  Set the group's exceptions
            this.selectedGroup.query.exceptions = exceptions

            //  Send the changes to the API
            const group = await this.$store.dispatch('groupsPut', this.selectedGroup)

            //  If the request failed, do nothing
            if (!group) return

            //  Notify the user of successful response
            this.$store.commit('appNotificationBarSet', { message: 'Group Updated' })

            //  Reset our checkbox
            this.$refs.personsList.multiSelectReset()
        },
        /**
         * Remove a person from a static group (ie delete their membership)
         *
         * @param {array}  person_ids - a list of person ids that we are removing
         * @param {boolean} flag   - bool that determines if the 'unsubscribe' message is sent when a person is removed from a campaign group
         */
        async removePersonsFromStaticGroup(person_ids, flag = null): Promise<void> {
            //  Remove the person's id from the group
            person_ids.forEach((x) => this.selectedGroup.persons_ids.remove(x))

            //  Attach flag to the request
            this.selectedGroup.flag = flag

            //  Send the update request to the API
            const response = await this.$store.dispatch('groupsPut', this.selectedGroup)

            //  If the request failed, do nothing
            if (!response) return

            //  Else, notify the user
            this.$store.commit('appNotificationBarSet', {
                message: `${person_ids.length} contact(s) have been removed`
            })

            //  Reset our checkbox
            this.$refs.personsList.multiSelectReset()
        },
        //  Search a list of persons
        searchPersons(persons: Person[], text: string): Person[] {
            //  If the search text is null or empty, return full list of persons
            if (!text) return persons

            //  Normalize search text with lowercase
            const lower = text.toLowerCase()

            //  Split multiple words into an array of words
            const split = lower.split(' ')

            //  Filter the list of persons based on the search text
            const filtered = persons.filter((x: Person) => {
                return split.every((y) => {
                    //  Search for the text in the folowing person attributes
                    const a = x.name_full && x.name_full.toLowerCase().indexOf(y) > -1
                    const b = x.name_given && x.name_given.toLowerCase().indexOf(y) > -1
                    const c = x.name_family && x.name_family.toLowerCase().indexOf(y) > -1
                    const d =
                        x.phone_default &&
                        x.phone_default.msisdn &&
                        x.phone_default.msisdn.indexOf(y) > -1
                    const e =
                        x.phone_default &&
                        x.phone_default.free_form_number &&
                        x.phone_default.free_form_number.indexOf(y) > -1
                    const f = x.company && x.company.toLowerCase().indexOf(y) > -1
                    const g =
                        x.email_default &&
                        x.email_default.address &&
                        x.email_default.address.indexOf(y) > -1

                    //  return a match if ANY of the values match
                    return a || b || c || d || e || f || g
                })
            })

            //  Return the filtered list
            return filtered
        },
        async searchQueryGroup(): Promise<void> {
            //  Grab our search text
            const text = this.searchtext

            //  Else, assemble the params required for a group persons search
            const params = { group_id: this.selectedGroup.id, searchtext: text }

            //  Send the request (We use Algolia search here as this workflow was bricked until I changed it to use persons search)
            const response = await this.$store.dispatch('personsSearch', params)

            //  Set our search results based on if this was successful or not
            this.searchResults = response || []

            //  Regardless of result, set our searched to true
            this.searched = true
        },
        searchStaticGroup(): void {
            //  Grab our search text
            const text = this.searchtext

            //  If the group is a static group, we can filter persons client-side
            const filtered = this.searchPersons(this.selectedGroup.persons, text)

            //  Set the search results
            this.searchResults = filtered

            //  Set searched to true to display our list
            this.searched = true
        },
        //  Unsets our group, persons, selected person, and selected user
        unsetGroup(): void {
            //  Unset our group (this unsets the persons in the watcher)
            this.selectedGroup = null

            //  Unset our persons
            this.selectedGroupPersons = []

            //  Unsetting the person will also unset the user in the watcher
            this.selectedPerson = null

            //  Reset our params
            this.$store.commit('listsSelectedGroupPersonsParamsSet', { page: 1 })
        }
    },
    watch: {
        async id(val: number): Promise<void> {
            //  Start loading
            this.loading = true

            //  Set our group to this new id
            await this.$store.dispatch('listsSelectedGroupGet', val)

            //  Stop loading
            this.loading = false
        },
        async selectedGroup(group: Group): Promise<void> {
            //  Check if this is a new group
            const isNew = !this.id || group.id != this.id

            //  If this is a NEW group, reset the UI
            if (isNew) this.unsetGroup()

            //  Check if the group is a 'query' group
            const query = group.query

            //  If we have a static group we should just return the persons
            if (!query) {
                //  Get the group's persons ids
                const ids = group.persons_ids

                //  If ids do NOT exist, do nothing
                if (!ids || ids.length === 0) return

                //  Start our loading state
                this.loading = true

                //  Else, fetch the group's persons
                await this.$store.dispatch('listsSelectedGroupPersonsWhereIn', ids)

                //  Stop the loading
                this.loading = false
            }
            //  Else, this IS a query group, so we need to paginate the group's persons
            else {
                //  Grab our current pagination params
                const params = this.params

                //  Set our query
                params.query = query

                //  Set our query in the params
                this.$store.commit('listsSelectedGroupPersonsParamsSet', params)

                //  Start our loading state
                this.loading = true

                //  Paginate
                await this.$store.dispatch('listsSelectedGroupPersonsGet')

                //  Stop the loading
                this.loading = false
            }
        },
        scrollPositionLeft(position: ScrollPosition): void {
            //  Grab our selected group
            const group = this.selectedGroup

            //  Our conditions to paginate include, not paginating, position bottom, and not static
            const proceed = !this.paginating && position.bottom && group.query

            //  If we are NOT paginating AND scroll is at bottom, paginate next page
            if (proceed) this.paginateCurrentList()
        }
    }
})
